import React from 'react';
import styled from 'styled-components';

import { getColor } from '../styles/themeGetters';
import type { ColorKeys } from '../styles/themes';

type ProgressBarOuterProps = {
  width: string;
};

type ProgressBarInnerProps = {
  value: number;
  color: ColorKeys;
};

type ProgressBarProps = {
  value?: number;
  width?: string;
  color?: ColorKeys;
  label: string;
};

const ProgressBarOuter = styled.div<ProgressBarOuterProps>`
  background-color: ${getColor('grey-light-100')};
  border-radius: 20px;
  width: ${({ width }) => width};
  height: 6px;

  /* Makes the background color of the progress bar slightly darker when used on non-white backgrounds. */
  mix-blend-mode: multiply;
`;

const ProgressBarInner = styled.div<ProgressBarInnerProps>`
  background-color: ${({ color }) => getColor(color)};
  height: 6px;
  width: ${({ value }) => `${value * 100}%`};
  border-radius: 3px ${({ value }) => (value > 0.96 ? '3px' : 0)}
    ${({ value }) => (value > 0.96 ? '3px' : 0)} 3px;
`;

const ProgressBar = ({
  value = 0,
  width = '60px',
  color = 'navy',
  label,
}: ProgressBarProps) => {
  // The progress bar value must be a number between 0 (no progress) and 1 (100% progress).
  // cappedValue is a safe value that ensures any number outside the valid range will be
  // set accordingly to the lowest or the highest point of the range.
  const cappedValue = Math.min(Math.max(value, 0), 1);

  return (
    <ProgressBarOuter
      width={width}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(cappedValue * 100)}
    >
      <ProgressBarInner value={cappedValue} color={color} />
    </ProgressBarOuter>
  );
};

export { ProgressBar };
